import dayjs from 'dayjs'
import { compact, maxBy, sum } from 'lodash'
import { useMemo } from 'react'

import type {
  BatteryModel,
  BatteryUptimeModel,
  ChargeEvent,
  DerAggregateField,
  DerSampleField,
  DerSnapshotResponse,
  GetDerSamplesArgs,
  GetFuelTankSamplesArgs
} from '~/data/api/client/ders'
import {
  useGetBatteryUptimeQuery,
  useGetGridConnectionQuery,
  useGetLoadQuery,
  useGetPanelQuery,
  useGetDerAggregatesQuery,
  useGetDerSamplesQuery,
  useGetDerSnapshotQuery,
  useGetFuelTankAggregatesQuery,
  useGetFuelTankSamplesQuery,
  useGetFuelTankSnapshotQuery
} from '~/data/api/client/ders'
import { type UsePollingOptions, usePolling } from '~/data/api/hooks/polling'
import { useNowMemo } from '~/data/time/hooks'
import type { TimeRange } from '~/data/time/types'

import {
  useManagedBatteries,
  useManagedFuelTanks,
  useManagedGenerators,
  useManagedGridConnections,
  useManagedLoads,
  useManagedSolarArrays
} from '../manager'

/******************************************************************************
 * Batteries — IndexedDB-backed accessors
 ******************************************************************************/

type DerFilters = { orgId?: string; siteId?: string; nodeId?: string }

type UseBatteriesInput = { args?: DerFilters }

export function useBatteries({ args }: UseBatteriesInput = {}) {
  return useManagedBatteries({ filters: args })
}

type UseBatteryInput = { args?: { id?: string } }

export function useBattery({ args }: UseBatteryInput = {}) {
  const indexedDbBatteries = useManagedBatteries()

  return useMemo(() => {
    const { batteries, ...rest } = indexedDbBatteries
    return { ...rest, battery: batteries.find((b) => b.id === args?.id) }
  }, [indexedDbBatteries, args?.id])
}

/******************************************************************************
 * Generators — IndexedDB-backed accessors
 ******************************************************************************/

type UseGeneratorsInput = { args?: DerFilters }

export function useGenerators({ args }: UseGeneratorsInput = {}) {
  return useManagedGenerators({ filters: args })
}

type UseGeneratorInput = { args?: { id?: string } }

export function useGenerator({ args }: UseGeneratorInput = {}) {
  const indexedDbGenerators = useManagedGenerators()

  return useMemo(() => {
    const { generators, ...rest } = indexedDbGenerators
    return { ...rest, generator: generators.find((g) => g.id === args?.id) }
  }, [indexedDbGenerators, args?.id])
}

/******************************************************************************
 * Solar arrays — IndexedDB-backed accessors
 ******************************************************************************/

type UseSolarArraysInput = { args?: DerFilters }

export function useSolarArrays({ args }: UseSolarArraysInput = {}) {
  return useManagedSolarArrays({ filters: args })
}

type UseSolarArrayInput = { args?: { id?: string } }

export function useSolarArray({ args }: UseSolarArrayInput = {}) {
  const indexedDbSolarArrays = useManagedSolarArrays()

  return useMemo(() => {
    const { solarArrays, ...rest } = indexedDbSolarArrays
    return { ...rest, solarArray: solarArrays.find((s) => s.id === args?.id) }
  }, [indexedDbSolarArrays, args?.id])
}

/******************************************************************************
 * Fuel tanks — IndexedDB-backed accessors
 ******************************************************************************/

type UseFuelTanksInput = { args?: DerFilters }

export function useFuelTanks({ args }: UseFuelTanksInput = {}) {
  return useManagedFuelTanks({ filters: args })
}

type UseFuelTankInput = { args?: { id?: string } }

export function useFuelTank({ args }: UseFuelTankInput = {}) {
  const indexedDbFuelTanks = useManagedFuelTanks()

  return useMemo(() => {
    const { fuelTanks, ...rest } = indexedDbFuelTanks
    return { ...rest, fuelTank: fuelTanks.find((f) => f.id === args?.id) }
  }, [indexedDbFuelTanks, args?.id])
}

/******************************************************************************
 * Grid connections — IndexedDB-backed accessors
 ******************************************************************************/

type UseGridConnectionsInput = { args?: DerFilters }

export function useGridConnections({ args }: UseGridConnectionsInput = {}) {
  return useManagedGridConnections({ filters: args })
}

type UseGridConnectionInput = { args?: { id?: string } }

export function useGridConnection({ args }: UseGridConnectionInput = {}) {
  const indexedDbGridConnections = useManagedGridConnections()

  return useMemo(() => {
    const { gridConnections, ...rest } = indexedDbGridConnections
    return { ...rest, gridConnection: gridConnections.find((g) => g.id === args?.id) }
  }, [indexedDbGridConnections, args?.id])
}

/******************************************************************************
 * Loads — IndexedDB-backed accessors
 ******************************************************************************/

type UseLoadsInput = { args?: DerFilters }

export function useLoads({ args }: UseLoadsInput = {}) {
  return useManagedLoads({ filters: args })
}

type UseLoadInput = { args?: { id?: string } }

export function useLoad({ args }: UseLoadInput = {}) {
  const indexedDbLoads = useManagedLoads()

  return useMemo(() => {
    const { loads, ...rest } = indexedDbLoads
    return { ...rest, load: loads.find((l) => l.id === args?.id) }
  }, [indexedDbLoads, args?.id])
}

/******************************************************************************
 * Bound energy entities
 ******************************************************************************/

/** The energy entities a connected device (today, a meter) can be bound to. */
export type BoundEntityType = 'panel' | 'grid_connection' | 'load'

export type BoundEnergyEntity = {
  id: string
  /** Display-ready: falls back to the id when the entity has no name. */
  name: string
  nodeId?: string
}

/**
 * What to call a bound entity. The name is optional on every one of these
 * models, and an edit that clears it persists an empty string rather than
 * dropping the field, so "absent" covers both `undefined` and `''` and `??` is
 * the wrong operator. Falls back to the id, which is at least identifying.
 */
function boundEntityDisplayName(entity: { id: string; name?: string | null }): string {
  return entity.name?.trim() || entity.id
}

/**
 * Resolve the single energy entity a device is bound to, whichever kind it is.
 *
 * Fetches the one entity by id, the same way panel bindings have always
 * resolved. Deliberately not the IndexedDB accessors: those are gated on
 * `grid-connections-in-ui` / `loads-in-ui` and withhold their cache when a gate
 * is off, which would blank the name of a binding the device already carries.
 * The gates govern featured surfaces -- nav, lists, detail pages -- not the
 * display of data a meter is already holding an id for.
 */
export function useBoundEnergyEntity(
  entityType: BoundEntityType,
  entityId: string | undefined,
  { skip = false }: { skip?: boolean } = {}
): { entity?: BoundEnergyEntity; isLoading: boolean } {
  const resolve = !skip && !!entityId

  const { data: gridConnection, isLoading: isLoadingGridConnection } = useGetGridConnectionQuery(
    { gridConnectionId: entityId ?? '' },
    { skip: !resolve || entityType !== 'grid_connection' }
  )
  const { data: load, isLoading: isLoadingLoad } = useGetLoadQuery(
    { loadId: entityId ?? '' },
    { skip: !resolve || entityType !== 'load' }
  )
  const { data: panel, isLoading: isLoadingPanel } = useGetPanelQuery(
    { panelId: entityId ?? '' },
    { skip: !resolve || entityType !== 'panel' }
  )

  return useMemo(() => {
    if (!resolve || !entityId) return { entity: undefined, isLoading: false }
    const resolved = entityType === 'grid_connection' ? gridConnection : entityType === 'load' ? load : panel
    return {
      entity: resolved
        ? { id: entityId, name: boundEntityDisplayName({ ...resolved, id: entityId }), nodeId: resolved.nodeId }
        : undefined,
      isLoading: isLoadingGridConnection || isLoadingLoad || isLoadingPanel
    }
  }, [
    resolve,
    entityId,
    entityType,
    gridConnection,
    load,
    panel,
    isLoadingGridConnection,
    isLoadingLoad,
    isLoadingPanel
  ])
}


/******************************************************************************
 * GET /api/v1/fuel-tanks/{fuelTankId}/snapshot
 *
 * Fuel tanks report level readings through their own component-typed endpoints
 * rather than the DER-agnostic snapshot/samples surface, so they get their own
 * hooks instead of reusing `useDerSnapshot` / `useDerSamples`.
 ******************************************************************************/

export function useFuelTankSnapshot(fuelTankId: string, options?: UsePollingOptions) {
  return usePolling(useGetFuelTankSnapshotQuery, { fuelTankId }, options)
}

/******************************************************************************
 * GET /api/v1/fuel-tanks/{fuelTankId}/timespan/samples
 ******************************************************************************/

type UseFuelTankSamplesArgs = {
  fuelTankId: string
  timeRange: TimeRange
  resolution?: GetFuelTankSamplesArgs['resolution']
}

export function useFuelTankSamples(args: UseFuelTankSamplesArgs, options: UsePollingOptions = {}) {
  const result = usePolling(
    useGetFuelTankSamplesQuery,
    {
      fuelTankId: args.fuelTankId,
      page: 0,
      count: FUEL_TANK_SAMPLE_PAGE_SIZE,
      from: args.timeRange.from.toISOString(),
      to: args.timeRange.to.toISOString(),
      resolution: args.resolution ?? fuelTankSampleResolution(args.timeRange),
      order: 'asc'
    },
    options
  )

  return { ...result, samples: result.data?.data }
}

const FUEL_TANK_SAMPLE_PAGE_SIZE = 3000

/**
 * Smallest bucket size whose bucket count for the range fits in a single
 * {@link FUEL_TANK_SAMPLE_PAGE_SIZE}-item page, so charts always see the whole
 * requested window. Same rule the meter samples hook uses.
 */
export function fuelTankSampleResolution(timeRange: TimeRange): NonNullable<GetFuelTankSamplesArgs['resolution']> {
  const minutes = timeRange.to.diff(timeRange.from, 'minutes')
  if (minutes <= FUEL_TANK_SAMPLE_PAGE_SIZE) return '1m'
  if (minutes <= FUEL_TANK_SAMPLE_PAGE_SIZE * 5) return '5m'
  if (minutes <= FUEL_TANK_SAMPLE_PAGE_SIZE * 15) return '15m'
  return '1h'
}

/******************************************************************************
 * GET /api/v1/fuel-tanks/{fuelTankId}/timespan/aggregates
 ******************************************************************************/

export function useFuelTankAggregates(fuelTankId: string, timeRange: TimeRange, options: UsePollingOptions = {}) {
  return usePolling(
    useGetFuelTankAggregatesQuery,
    {
      fuelTankId,
      from: timeRange.from.toISOString(),
      to: timeRange.to.toISOString()
    },
    options
  )
}

/******************************************************************************
 * GET /api/ders/{derId}/snapshot
 ******************************************************************************/

export function useDerSnapshot(derId: string, options?: UsePollingOptions) {
  return usePolling(useGetDerSnapshotQuery, { derId }, options)
}

/******************************************************************************
 * GET /api/ders/{derId}/aggregates
 ******************************************************************************/

/**
 * Fetches a DER's energy aggregates over the last 24 hours and the prior 24
 * hours in parallel, so callers can render a value with a trend arrow against
 * the previous period. The endpoint is DER-agnostic — the same fields
 * (`energyImported`, `energyExported`, etc.) come back regardless of whether
 * the DER is a battery or a generator.
 */
export function useDerEnergyAggregates(derId: string) {
  const now = useNowMemo()
  const ranges = useMemo(() => {
    const dayAgo = now.subtract(24, 'hours')
    return {
      current: { from: dayAgo.toISOString(), to: now.toISOString() },
      prior: { from: dayAgo.subtract(24, 'hours').toISOString(), to: dayAgo.toISOString() }
    }
  }, [now])

  const { data: current } = useGetDerAggregatesQuery({ derId, ...ranges.current })
  const { data: prior } = useGetDerAggregatesQuery({ derId, ...ranges.prior })
  return { current, prior }
}

// Stable reference so the query arg doesn't change identity every render.
const ENERGY_PRODUCED_FIELDS: DerAggregateField[] = ['energyProduced']

/**
 * Fetches a DER's produced-energy aggregate (`energyProduced` — LAG-deltas over
 * the cumulative production register solar arrays publish) for the given range,
 * plus the immediately-prior range of equal length in parallel, so callers can
 * render a value with a trend arrow against the previous period. Unlike
 * {@link useDerEnergyAggregates} the range is caller-controlled, letting the
 * tile track a chart's date picker.
 */
export function useDerEnergyProduced(derId: string, timeRange: TimeRange) {
  const ranges = useMemo(() => {
    const spanMs = timeRange.to.diff(timeRange.from)
    return {
      current: { from: timeRange.from.toISOString(), to: timeRange.to.toISOString() },
      prior: { from: timeRange.from.subtract(spanMs, 'millisecond').toISOString(), to: timeRange.from.toISOString() }
    }
  }, [timeRange])

  const current = useGetDerAggregatesQuery({ derId, ...ranges.current, fields: ENERGY_PRODUCED_FIELDS })
  const prior = useGetDerAggregatesQuery({ derId, ...ranges.prior, fields: ENERGY_PRODUCED_FIELDS })
  return { current, prior }
}

/******************************************************************************
 * GET /api/batteries/{batteryId}/uptime
 ******************************************************************************/

type UptimeStatuses = { breakdown: Record<ChargeEvent, number>; percentage: number }
export type BessUptime = {
  current: UptimeStatuses
  prior: UptimeStatuses
}

type UseUptimeOptions = {
  /**
   * Second GET shifted back by one day so callers can compare windows.
   * Overlay longer ranges turn this off — they don't show a trend.
   */
  comparePrior?: boolean
  skip?: boolean
}

export function useUptime(battery: Pick<BatteryModel, 'id'>, timeRange: TimeRange | undefined, options: UseUptimeOptions = {}) {
  const { comparePrior = true, skip = false } = options
  const currentPeriod = useGetBatteryUptimeQuery(
    {
      batteryId: battery.id,
      from: timeRange?.from.toISOString() ?? '',
      to: timeRange?.to.toISOString() ?? ''
    },
    { skip: skip || !timeRange }
  )

  const priorPeriod = useGetBatteryUptimeQuery(
    {
      batteryId: battery.id,
      from: timeRange?.from.subtract(1, 'day').toISOString() ?? '',
      to: timeRange?.to.subtract(1, 'day').toISOString() ?? ''
    },
    { skip: skip || !timeRange || !comparePrior }
  )

  // `isFetching`, not `isLoading` — switching windows re-runs the query with new
  // args, and callers need to distinguish "still arriving" from "reported nothing".
  const isFetching = currentPeriod.isFetching
  return useMemo<BessUptime & { isFetching: boolean }>(() => {
    return {
      current: parseUptime(currentPeriod.data),
      prior: parseUptime(priorPeriod.data),
      isFetching
    }
  }, [currentPeriod.data, priorPeriod.data, isFetching])
}

/**
 * Trailing window for a battery's headline uptime figure. Matches the 7 days
 * the site page reports charger uptime over, so the two pages can't disagree
 * about the same week.
 */
export const UPTIME_WINDOW_DAYS = 7

/**
 * A battery's uptime over {@link UPTIME_WINDOW_DAYS}. One GET — no prior-window
 * comparison, because the surfaces that read this show a single figure rather
 * than a trend, and a period-over-period delta on a ratio reads as a bigger
 * swing than it is (68% vs 36% renders as "+89%").
 */
export function useTrailingUptime(battery: Pick<BatteryModel, 'id'>) {
  const now = useNowMemo()
  const timeRange = useMemo(
    () => ({ from: now.subtract(UPTIME_WINDOW_DAYS, 'days'), to: now }),
    [now]
  )
  return useUptime(battery, timeRange, { comparePrior: false })
}

function parseUptime(uptime?: BatteryUptimeModel) {
  const breakdown = Object.entries(uptime?.uptime ?? {}).reduce(
    (acc, [status, value]) => {
      if (isBatteryStatus(status)) {
        acc[status] = (acc[status] ?? 0) + value
      }
      return acc
    },
    {} as Record<ChargeEvent, number>
  )

  const total = sum(Object.values(breakdown))
  const downtime = sum(Object.entries(breakdown).map(([status, value]) => (isDowntimeStatus(status) ? value : 0)))

  return {
    breakdown,
    percentage: total > 0 ? 1 - downtime / total : 0
  }
}

export function isBatteryStatus(status: string): status is ChargeEvent {
  return ['CHARGING', 'DISCHARGING', 'IDLE', 'MAX_CHARGE', 'MIN_CHARGE', 'FAULTED', 'UNKNOWN'].includes(status)
}

function isDowntimeStatus(status: string) {
  return ['FAULTED', 'UNKNOWN'].includes(status)
}

/******************************************************************************
 * GET /api/ders/{derId}/samples
 ******************************************************************************/

type UseDerSamplesArgs = {
  derId: string
  timeRange: TimeRange
  fields: DerSampleField[]
  resolution?: GetDerSamplesArgs['resolution']
}

export function useDerSamples(args: UseDerSamplesArgs, options: UsePollingOptions = {}) {
  const result = usePolling(
    useGetDerSamplesQuery,
    {
      derId: args.derId,
      page: 0,
      count: 3000,
      from: args.timeRange.from.toISOString(),
      to: args.timeRange.to.toISOString(),
      fields: args.fields,
      resolution: args.resolution ?? '1m',
      order: 'asc'
    },
    options
  )

  return { ...result, samples: result.data?.data }
}

/**
 * Returns the most recent `eventTime` across a DER's snapshot sub-fields —
 * power, DC power, storage, engine, and operational state. DER types that don't
 * carry one of these (e.g. generators have no storage, only solar arrays publish
 * dcPower, only generators publish engine) contribute fewer candidates;
 * `compact` drops the absent ones.
 */
export function useLastHeard(snapshot?: DerSnapshotResponse) {
  return useMemo(() => {
    const candidates = compact([
      snapshot?.power?.eventTime,
      snapshot?.dcPower?.eventTime,
      snapshot?.storage?.eventTime,
      snapshot?.engine?.eventTime,
      snapshot?.operationalState?.eventTime
    ])
    return maxBy(candidates, (t) => dayjs(t).valueOf())
  }, [snapshot])
}
